import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfile } from 'api/account/models/UserProfile';
import { POLICIES } from 'Config';
import { Reducers } from 'store/types';
import Sidebar, { SidebarItem } from '../sidebar/Sidebar';
import styles from './AuthenticatedLayout.module.scss';
import UsersService from 'api/users/UsersService';
import { AlertsTotalsDto } from 'api/alerts/models/AlertsTotalsDto';
import { updateTotals } from 'store/alerts/action';
import NewAccountScreen from 'screens/new_account/NewAccountScreen';
import PermissionsChangedBanner from 'common/components/permissionsChangedBanner/PermissionsChangedBanner';
import AnnouncementsProvider from 'common/components/announcements/AnnouncementsProvider';
import AnnouncementsBanner from 'common/components/announcements/AnnouncementsBanner';
import Storage from 'common/services/Storage';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';
import {
    FiGrid,
    FiTruck,
    FiMapPin,
    FiTool,
    FiBell,
    FiBriefcase,
    FiMail,
    FiShield,
    FiAlertTriangle,
    FiUsers,
    FiUserCheck,
    FiSettings,
    FiServer,
} from 'react-icons/fi';

type Props = {
    children: React.ReactNode;
}

type SidebarItemWithValidation = {
    policies: (keyof typeof POLICIES)[];
    type?: 'OR' | 'AND';
    icon?: React.ReactNode;
} & Omit<SidebarItem, 'children'>;

type SidebarGroupWithValidation = {
    text: string;
    icon?: React.ReactNode;
    children: SidebarItemWithValidation[];
};

type SidebarElement = SidebarItemWithValidation | SidebarGroupWithValidation;

const isGroup = (item: SidebarElement): item is SidebarGroupWithValidation => {
    return 'children' in item && Array.isArray(item.children);
};

const SIDEBAR_COLLAPSED_KEY = 'SIDEBAR_COLLAPSED';

const AuthenticatedLayout: React.FunctionComponent<Props> = ({ children }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const isCompanyInfoFilled = useSelector<Reducers, boolean>(state => state.authentication.isCompanyInfoFilled);
    const hasAlertsPolicy = UsersService.hasPolicies(loggedUser?.policies || [], ['SETTINGUP_ALERTS_READ']);
    const isPlatformAdmin = UsersService.isPlatformAdmin(loggedUser?.roles);
    const alertsTotals = useSelector<Reducers, AlertsTotalsDto>(state => state.alerts.totals)
    const policiesChanged = useSelector<Reducers, boolean>(state => state.authentication.policiesChanged);
    const userIssues = loggedUser?.issues || [];
    const canApproveReservations = UsersService.hasPolicies(loggedUser?.policies || [], ['SETTINGUP_RESERVATIONS_APPROVE']);
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

    const [collapsed, setCollapsed] = useState(() => {
        return Storage.get(SIDEBAR_COLLAPSED_KEY) === 'true';
    });

    const toggleCollapse = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            Storage.set(SIDEBAR_COLLAPSED_KEY, String(next));
            return next;
        });
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty('--sidebar-width', collapsed ? '64px' : '270px');
    }, [collapsed]);

    const validateItem = useCallback((item: SidebarItemWithValidation) => {
        const type = item.type || 'AND';
        const policies = item.policies || [];
        const userPolicies = loggedUser?.policies || [];

        return UsersService.hasPolicies(userPolicies, policies, type);
    }, [loggedUser]);

    useEffect(() => {
        if (hasAlertsPolicy)
            dispatch(updateTotals());

        if (canApproveReservations) {
            VehicleReservationsService.getAllRequests({ itemsPerPage: 1, page: 1, status: 'PENDING_APPROVAL' })
                .then(result => setPendingRequestsCount(result.totalItems))
                .catch(() => { /* silently fail */ });
        }
    }, [])

    const sidebarItems: SidebarItem[] = useMemo(() => {
        if (userIssues.length > 0) {
            return [];
        }

        const items: SidebarElement[] = [
            // Raiz - Topo: Painel de Controlo
            {
                text: t('dashboard.title'),
                url: '/dashboard',
                policies: ['SETTINGUP_DASHBOARDS_READ'],
                type: 'OR',
                icon: <FiGrid />,
            },

            // Grupo: Frota
            {
                text: t('menu.group_fleet'),
                icon: <FiTruck />,
                children: [
                    {
                        text: t('menu.vehicles'),
                        url: '/vehicles',
                        policies: ['SETTINGUP_VEHICLES_READ', 'SETTINGUP_VEHICLES_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.vehicles_management'),
                        url: '/vehicles-management',
                        policies: ['SETTINGUP_VEHICLES_MANAGEMENT_READ', 'SETTINGUP_VEHICLES_MANAGEMENT_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.vehicles_mileage'),
                        url: '/vehicles-mileage',
                        policies: ['SETTINGUP_VEHICLES_MILEAGE_READ', 'SETTINGUP_VEHICLES_MILEAGE_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.contracts'),
                        url: '/contracts',
                        policies: ['SETTINGUP_CONTRACTS_READ', 'SETTINGUP_CONTRACTS_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.drivers'),
                        url: '/drivers',
                        policies: ['SETTINGUP_DRIVERS_READ'],
                        type: 'OR',
                    },
                ],
            },

            // Grupo: Localização
            {
                text: t('menu.group_location'),
                icon: <FiMapPin />,
                children: [
                    {
                        text: t('menu.vehicles_gps'),
                        url: '/vehicles-gps',
                        policies: ['SETTINGUP_GPS_READ'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.geofences'),
                        url: '/geofences',
                        policies: ['SETTINGUP_COST_CENTER_READ', 'SETTINGUP_COST_CENTER_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.alarms'),
                        url: '/alarms',
                        policies: ['SETTINGUP_GPS_READ'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.visits_report'),
                        url: '/visits-report',
                        policies: ['SETTINGUP_GPS_READ'],
                        type: 'OR',
                    },
                ],
            },

            // Grupo: Operações
            {
                text: t('menu.group_operations'),
                icon: <FiTool />,
                children: [
                    {
                        text: t('menu.costs'),
                        url: '/charges',
                        policies: ['SETTINGUP_CHARGES_READ', 'SETTINGUP_CHARGES_WRITE'],
                        type: 'OR',
                    },
                    // #66600 — consumo vs plafond da frota. Fica junto a Custos por ser a mesma leitura.
                    {
                        text: t('menu.plafonds'),
                        url: '/plafonds-report',
                        policies: ['SETTINGUP_DASHBOARDS_READ'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.maintenances'),
                        url: '/maintenances',
                        policies: ['SETTINGUP_VEHICLE_MAINTENANCES_READ', 'SETTINGUP_VEHICLE_MAINTENANCES_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.verifications'),
                        url: '/verifications',
                        policies: ['SETTINGUP_VERIFICATIONS_READ', 'SETTINGUP_VERIFICATIONS_WRITE'],
                        type: 'OR',
                    },
                    {
                        text: t('menu.reservations'),
                        url: '/reservations',
                        policies: ['SETTINGUP_RESERVATIONS_READ', 'SETTINGUP_RESERVATIONS_WRITE'],
                        type: 'OR',
                        badgeValue: pendingRequestsCount || undefined,
                        badgeIcon: false,
                    },
                ],
            },

            // Grupo: Notificações
            {
                text: t('menu.group_notifications'),
                icon: <FiBell />,
                children: [
                    {
                        text: t('menu.notifications'),
                        url: '/alerts',
                        policies: ['SETTINGUP_ALERTS_READ'],
                        type: 'OR',
                        badgeValue: alertsTotals.totalNotReadOwn || undefined,
                    },
                    {
                        text: t('menu.reminders'),
                        url: '/reminders',
                        policies: ['SETTINGUP_REMINDER_READ', 'SETTINGUP_REMINDER_WRITE'],
                        type: 'OR',
                    },
                ],
            },

            // Raiz - Fundo: Administração
            {
                text: t('menu.companies'),
                url: '/companies',
                policies: ['SETTINGUP_COMPANIES_READ', 'SETTINGUP_COMPANIES_WRITE'],
                type: 'OR',
                icon: <FiBriefcase />,
            },
            {
                text: t('menu.avisos'),
                url: '/avisos',
                policies: ['SETTINGUP_COMPANIES_READ', 'SETTINGUP_COMPANIES_WRITE'],
                type: 'OR',
                icon: <FiAlertTriangle />,
            },
            {
                text: t('menu.notifications_default'),
                url: '/notifications',
                policies: ['SETTINGUP_COMPANIES_READ', 'SETTINGUP_COMPANIES_WRITE'],
                type: 'OR',
                icon: <FiMail />,
            },
            {
                text: t('menu.rules'),
                url: '/rules',
                policies: ['SETTINGUP_COMPANIES_READ', 'SETTINGUP_COMPANIES_WRITE'],
                type: 'OR',
                icon: <FiShield />,
            },
            {
                text: t('menu.roles'),
                url: '/roles',
                policies: ['SETTINGUP_COMPANIES_READ', 'SETTINGUP_COMPANIES_WRITE'],
                type: 'OR',
                icon: <FiUsers />,
            },
            {
                text: t('menu.security'),
                url: '/security',
                policies: ['SETTINGUP_USERS_READ', 'SETTINGUP_USERS_WRITE'],
                type: 'OR',
                icon: <FiUserCheck />,
            },
            {
                text: t('menu.settings'),
                url: '/settings',
                policies: [
                    'SETTINGUP_GENERIC_TYPES_READ', 'SETTINGUP_GENERIC_TYPES_WRITE',
                    'SETTINGUP_VEHICLE_SEGMENTS_READ', 'SETTINGUP_VEHICLE_SEGMENTS_WRITE',
                    'SETTINGUP_VEHICLE_BRANDS_READ', 'SETTINGUP_VEHICLE_BRANDS_WRITE',
                    'SETTINGUP_VEHICLE_MODELS_READ', 'SETTINGUP_VEHICLE_MODELS_WRITE',
                    'SETTINGUP_SUPPLIERS_READ', 'SETTINGUP_SUPPLIERS_WRITE',
                    'SETTINGUP_LOCALS_READ', 'SETTINGUP_LOCALS_WRITE',
                    'SETTINGUP_DIAGNOSTIC_TYPES_READ', 'SETTINGUP_DIAGNOSTIC_TYPES_WRITE',
                    'SETTINGUP_COMMUNICATIONS_READ', 'SETTINGUP_COMMUNICATIONS_WRITE',
                    'SETTINGUP_NOTIFICATIONS_READ', 'SETTINGUP_NOTIFICATIONS_WRITE'
                ],
                type: 'OR',
                icon: <FiSettings />,
            },
            ...(isPlatformAdmin ? [{
                text: t('menu.platform_settings'),
                url: '/platform/system-settings',
                policies: ['SETTINGUP_COMPANIES_WRITE'] as (keyof typeof POLICIES)[],
                type: 'OR' as const,
                icon: <FiServer />,
            }] : []),
        ];

        // Filtrar items e grupos com base nas permissões
        const filteredItems: SidebarItem[] = [];

        items.forEach(item => {
            if (isGroup(item)) {
                // Filtrar children do grupo
                const visibleChildren = item.children.filter(validateItem);
                if (visibleChildren.length > 0) {
                    filteredItems.push({
                        text: item.text,
                        icon: item.icon,
                        children: visibleChildren.map(child => ({
                            text: child.text,
                            url: child.url,
                            badgeValue: child.badgeValue,
                            badgeIcon: child.badgeIcon,
                        })),
                    });
                }
            } else {
                if (validateItem(item)) {
                    filteredItems.push({
                        text: item.text,
                        url: item.url,
                        badgeValue: item.badgeValue,
                        badgeIcon: item.badgeIcon,
                        icon: item.icon,
                    });
                }
            }
        });

        return filteredItems;
    }, [loggedUser, validateItem, alertsTotals, pendingRequestsCount, t, userIssues]);

    return (
        <AnnouncementsProvider>
            <div className={styles.container} >
                <div className={`${styles.sidebarContainer} ${collapsed ? styles.sidebarCollapsed : ''}`}>
                    <Sidebar items={sidebarItems} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
                </div>
                <div className={styles.contentContainer} id="scrollHere">
                    {policiesChanged && <PermissionsChangedBanner />}
                    <AnnouncementsBanner />
                    {isCompanyInfoFilled && children}
                    {!isCompanyInfoFilled && <NewAccountScreen />}
                </div>
            </div>
        </AnnouncementsProvider>
    );
}

export default AuthenticatedLayout;
